import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('aira_token'));

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const storedToken = localStorage.getItem('aira_token');
    if (storedToken) {
      try {
        const response = await axios.get(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${storedToken}` },
          withCredentials: true
        });
        setUser(response.data);
        setToken(storedToken);
      } catch (error) {
        localStorage.removeItem('aira_token');
        setToken(null);
        setUser(null);
      }
    } else {
      // Try cookie-based auth
      try {
        const response = await axios.get(`${API_URL}/api/auth/me`, {
          withCredentials: true
        });
        setUser(response.data);
      } catch (error) {
        setUser(null);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, {
      email,
      password
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('aira_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, {
      name,
      email,
      password
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('aira_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const registerExtended = async (userData) => {
    const response = await axios.post(`${API_URL}/api/auth/register/extended`, userData);
    const { access_token, user: newUser } = response.data;
    localStorage.setItem('aira_token', access_token);
    setToken(access_token);
    setUser(newUser);
    return newUser;
  };

  const sendMobileOTP = async (mobileNumber) => {
    const response = await axios.post(`${API_URL}/api/auth/mobile/send-otp`, { 
      mobile_number: mobileNumber 
    });
    return response.data;
  };

  const verifyMobileOTP = async (mobileNumber, otp) => {
    const response = await axios.post(`${API_URL}/api/auth/mobile/verify-otp`, {
      mobile_number: mobileNumber,
      otp: otp
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('aira_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const initiateRecovery = async (recoveryType, contact) => {
    const response = await axios.post(`${API_URL}/api/auth/recovery/initiate`, {
      recovery_type: recoveryType,
      contact: contact
    });
    return response.data;
  };

  const resetPasswordWithToken = async (resetToken, newPassword) => {
    const response = await axios.post(`${API_URL}/api/auth/recovery/reset-password`, {
      token: resetToken,
      new_password: newPassword
    });
    return response.data;
  };

  const loginWithGoogle = () => {
    // Show loading state could be set here if needed
    // The Emergent Auth will redirect back with session_id in the hash
    // which is caught by AppRouter and handled by AuthCallback
    const currentUrl = window.location.origin + window.location.pathname;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(currentUrl)}`;
  };

  const handleOAuthCallback = async (sessionId) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/session`,
        { session_id: sessionId },
        { withCredentials: true }
      );
      setUser(response.data);
      if (response.data.session_token) {
        localStorage.setItem('aira_token', response.data.session_token);
        setToken(response.data.session_token);
      }
      return response.data;
    } catch (error) {
      console.error('OAuth callback error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('aira_token');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    registerExtended,
    sendMobileOTP,
    verifyMobileOTP,
    initiateRecovery,
    resetPasswordWithToken,
    loginWithGoogle,
    handleOAuthCallback,
    logout,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
